import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Divider, Typography } from "@mui/material";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { useNavigate, useOutletContext } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import TermDialog from "../../components/TermDialog";
import { endPoint, endPointServer } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import axios from "axios";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";
import ContractPDF from "../../utils/ContractPDF";

const poppins = { fontFamily: "'Poppins', sans-serif" };

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const whiteCard = {
  backgroundColor: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.95)",
  borderRadius: "16px",
};

const formatCouponRanges = (coupons) => {
  if (!coupons?.length) return "";

  const nums = [...new Set(coupons.map((c) => parseInt(c.coupon, 10)))]
    .filter((num) => !Number.isNaN(num))
    .sort((a, b) => a - b);

  if (!nums.length) return "";

  const ranges = [];
  let start = nums[0];
  let end = nums[0];

  for (let i = 1; i < nums.length; i++) {
    if (nums[i] === end + 1) {
      end = nums[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start} - ${end}`);
      start = nums[i];
      end = nums[i];
    }
  }

  ranges.push(start === end ? `${start}` : `${start} - ${end}`);

  return ranges.join(", ");
};

const getCouponText = (coupon, couponRange) => {
  if (couponRange?.totalCoupon) {
    return `${couponRange.couponStart} - ${couponRange.couponEnd} (${couponRange.totalCoupon} kupon)`;
  }

  return formatCouponRanges(coupon);
};

const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    py={1}
  >
    <Typography
      sx={{
        ...poppins,
        fontSize: "0.82rem",
        color: "rgba(15,31,69,0.55)",
        minWidth: 130,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        ...poppins,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: "#0f1f45",
        textAlign: "right",
        flex: 1,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default function Contract() {
  const { user } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const [contract, setContract] = useState([]);
  const [summary, setSummary] = useState([]);
  const [coupon, setCoupon] = useState([]);
  const [couponRange, setCouponRange] = useState(null);

  const [transactionNo, setTransactionNo] = useState("");
  const [saleId, setSaleId] = useState("");

  const [couponStatus, setCouponStatus] = useState("processing");
  const [salesId, setSalesId] = useState("");

  const [couponProgress, setCouponProgress] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [totalGenerated, setTotalGenerated] = useState(0);
  const [totalExpected, setTotalExpected] = useState(0);

  const isDisabled = summary.length === 0 || contract.length === 0;

  const couponText = getCouponText(coupon, couponRange);
  const hasCouponText = Boolean(couponText && couponText.trim() !== "");
  const isCouponReady = couponStatus === "done" && hasCouponText;

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const res = await axios.get(endPoint + "contract", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.msg) {
        setOpenAlert(true);
        setResponse(res.data.msg);
        setSuccess(true);
      }

      setContract(res.data.package || []);
      setSummary(res.data.summary || []);
      setCoupon(res.data.coupon || []);
      setCouponRange(res.data.couponRange || null);

      setTransactionNo(res.data.transactionNo || "");
      setSaleId(res.data.saleId || "");

      setCouponStatus(res.data.couponStatus || "processing");
      setSalesId(res.data.salesId || res.data.saleId || "");

      setCouponProgress(Number(res.data.progress || 0));
      setCouponMessage(res.data.couponMessage || "");
      setTotalGenerated(Number(res.data.totalGenerated || 0));
      setTotalExpected(Number(res.data.totalExpected || 0));
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg ?? "Terjadi kesalahan.");
      setSuccess(false);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const activeSalesId = salesId || saleId;

    if (!activeSalesId) return;

    /**
     * Kalau failed, polling berhenti.
     * Kalau done tapi couponText belum ada, polling tetap lanjut supaya data kupon ke-refresh.
     */
    if (couponStatus === "failed") return;
    if (couponStatus === "done" && hasCouponText) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(endPoint + "couponStatus", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            salesId: activeSalesId,
          },
        });

        const data = res.data?.data || {};

        /**
         * Ambil jobStatus dulu karena coupon_jobs bisa pending/processing/done/failed.
         */
        const status = data.jobStatus || data.couponStatus || "processing";
        const progress = Number(data.progress || 0);
        const generated = Number(data.totalGenerated || 0);
        const expected = Number(data.totalExpected || 0);
        const message =
          data.jobMessage || data.couponMessage || data.errorMessage || "";

        setCouponStatus(status);
        setCouponProgress(progress);
        setCouponMessage(message);
        setTotalGenerated(generated);
        setTotalExpected(expected);

        if (status === "failed") {
          clearInterval(interval);
          await fetchData({ silent: true });
          return;
        }

        if (status === "done") {
          /**
           * Penting:
           * Saat status done, ambil ulang contract supaya couponRange/coupon masuk.
           * Tombol tetap belum aktif sampai couponText benar-benar ada.
           */
          await fetchData({ silent: true });
        }
      } catch (error) {
        console.log("Cek status kupon gagal:", error);
      }
    }, 3000);

    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesId, saleId, couponStatus, token, hasCouponText]);

  const handleRetryCoupon = async () => {
    const activeSalesId = saleId || salesId;

    if (!activeSalesId) {
      setOpenAlert(true);
      setResponse("Sales ID tidak ditemukan.");
      setSuccess(false);
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        endPoint + "coupon-retry",
        {
          salesId: activeSalesId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setCouponStatus(res.data.couponStatus || "processing");
      setCouponMessage(res.data.msg || "Kupon sedang dibuat ulang");
      setCouponProgress(0);
      setTotalGenerated(0);

      setOpenAlert(true);
      setResponse(res.data.msg || "Job kupon dijalankan ulang.");
      setSuccess(true);
    } catch (error) {
      setOpenAlert(true);
      setResponse(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Gagal menjalankan ulang kupon.",
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeal = async () => {
    if (loading) return;

    if (couponStatus === "failed") {
      await handleRetryCoupon();
      return;
    }

    /**
     * Fix bug:
     * Jangan lanjut bikin PDF kalau status done tapi nomor kupon belum masuk state.
     */
    if (!isCouponReady) {
      setOpenAlert(true);

      if (couponStatus === "done" && !hasCouponText) {
        setResponse("Nomor kupon sedang dimuat. Mohon tunggu sebentar.");
        fetchData({ silent: true });
      } else if (couponStatus === "pending") {
        setResponse("Kupon sedang antri. Mohon tunggu sampai diproses.");
      } else {
        setResponse(
          couponMessage || "Kupon sedang dibuat. Mohon tunggu sampai selesai.",
        );
      }

      setSuccess(false);
      return;
    }

    setLoading(true);

    try {
      const activeSalesId = saleId || salesId;

      if (!activeSalesId) {
        throw new Error("Sales ID tidak ditemukan");
      }

      const finalCouponText = getCouponText(coupon, couponRange);

      if (!finalCouponText || finalCouponText.trim() === "") {
        throw new Error("Nomor kupon belum tersedia. Mohon tunggu sebentar.");
      }

      const checkRes = await axios.get(
        `${endPoint}contract/check-pdf/${activeSalesId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      let fileUrl;

      if (checkRes.data.exists) {
        fileUrl = checkRes.data.pdfPath;
      } else {
        const pdfBlob = await pdf(
          <ContractPDF
            contract={contract}
            summary={summary}
            coupon={coupon}
            couponRange={couponRange}
            couponText={finalCouponText}
            info={user[0]}
            no={transactionNo}
          />,
        ).toBlob();

        const formData = new FormData();
        formData.append(
          "data",
          JSON.stringify({
            salesId: activeSalesId,
            userName: user[0]?.name || "",
          }),
        );
        formData.append("pdfFile", pdfBlob, "contract.pdf");

        await axios.post(`${endPoint}contract/upload-pdf`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        const confirmRes = await axios.get(
          `${endPoint}contract/check-pdf/${activeSalesId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!confirmRes.data.exists || !confirmRes.data.pdfPath) {
          throw new Error("PDF tidak ditemukan setelah upload");
        }

        fileUrl = confirmRes.data.pdfPath;
      }

      if (!fileUrl) {
        throw new Error("Gagal mendapatkan file PDF");
      }

      const phoneNo = user[0]?.phoneNo || "";
      const nama = user[0]?.name || "";
      const namaToko = user[0]?.storeName || "";
      const cleanFileUrl = fileUrl.replace(/\\/g, "/");
      const fullFileUrl = `${endPointServer}${
        cleanFileUrl.startsWith("/") ? "" : "/"
      }${cleanFileUrl}`;

      const formDataWA = new FormData();
      formDataWA.append("no_whatsapp", phoneNo);
      formDataWA.append("nama", nama);
      formDataWA.append("nama_toko", namaToko);
      formDataWA.append("url_dokumen", fullFileUrl);

      await axios.post(
        "https://alderon.zeinvitation.com/api/send-message",
        formDataWA,
      );

      navigate("/deal-success", {
        state: {
          transactionNo,
          contract,
          summary,
          coupon,
          couponRange,
          couponText: finalCouponText,
          pdfFileName: `Invoice_${transactionNo}.pdf`,
        },
      });
    } catch (error) {
      setOpenAlert(true);
      setResponse(
        error.response?.data?.msg ||
          error.message ||
          "Gagal menyelesaikan deal.",
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const totalPaket = contract.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0,
  );

  const getCouponInfoText = () => {
    if (isCouponReady) {
      return couponText;
    }

    if (couponStatus === "done" && !hasCouponText) {
      return "Nomor kupon sedang dimuat";
    }

    if (couponStatus === "failed") {
      return couponMessage || "Kupon gagal dibuat";
    }

    if (couponStatus === "pending") {
      return "Kupon sedang antri";
    }

    if (couponStatus === "processing") {
      if (totalExpected > 0) {
        return `${totalGenerated}/${totalExpected}`;
      }

      return "Kupon sedang dibuat";
    }

    return couponMessage || "Menunggu proses kupon";
  };

  const getButtonText = () => {
    if (loading) {
      return "Memproses...";
    }

    if (isCouponReady) {
      return "Lanjutkan";
    }

    if (couponStatus === "done" && !hasCouponText) {
      return "Memuat nomor kupon...";
    }

    if (couponStatus === "failed") {
      return "Coba Buat Kupon Lagi";
    }

    if (couponStatus === "pending") {
      return "Kupon sedang antri...";
    }

    if (couponStatus === "processing") {
      if (totalExpected > 0) {
        return `Membuat kupon... ${couponProgress}%`;
      }

      return "Membuat kupon...";
    }

    return "Menunggu kupon...";
  };

  const isButtonDisabled =
    loading ||
    isDisabled ||
    !isConfirmed ||
    (couponStatus !== "failed" && !isCouponReady);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        px: 2,
        py: 3,
        zIndex: 1,
        position: "relative",
      }}
    >
      <img
        src="SolarTuffNatGath.png"
        alt="Strength of Loyalty & Relationships"
        style={{ width: 80, objectFit: "contain" }}
      />

      <Typography
        sx={{
          ...poppins,
          fontWeight: 700,
          fontSize: "1.5rem",
          color: "#fff",
          mb: 0.5,
          textAlign: "center",
        }}
      >
        Konfirmasi Pesanan
      </Typography>

      <Typography
        sx={{
          ...poppins,
          fontWeight: 400,
          fontSize: "0.82rem",
          color: "rgba(255,255,255,0.75)",
          mb: 3,
          textAlign: "center",
        }}
      >
        Pastikan semua detail pesanan sudah benar
      </Typography>

      <Box sx={{ ...whiteCard, p: 2.5, mb: 2 }}>
        <Typography
          sx={{
            ...poppins,
            fontWeight: 700,
            fontSize: "0.9rem",
            color: "#0f1f45",
            mb: 1,
          }}
        >
          Detail Pesanan
        </Typography>

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)", mb: 1.5 }} />
        {console.log(contract[0])}
        <DetailRow label="Nama" value={user[0]?.name || "-"} />
        <DetailRow label="Nama Toko" value={user[0]?.storeName || "-"} />
        <DetailRow label="Nomor WhatsApp" value={user[0]?.phoneNo || "-"} />
        <DetailRow label="Nomor Transaksi" value={transactionNo || "-"} />
        <DetailRow
          label="Nominal Transaksi"
          value={
            contract[0] ? formatRupiah(contract[0].quantity * 10000000) : "-"
          }
        />
        <DetailRow
          label="Extra Diskon"
          value={contract[0] ? `${contract[0].packageId}%` : "-"}
        />

        <DetailRow
          label="Uang Elektronik"
          value={contract[0] ? formatRupiah(contract[0].uangElek) : "-"}
        />

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)", my: 1 }} />

        {contract.map((item, index) => (
          <DetailRow
            key={`${item.packageId}-${index}`}
            label={item.packageName}
            value={`${item.quantity} paket`}
          />
        ))}

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)", my: 1 }} />

        <DetailRow label="Total Paket" value={`${totalPaket} paket`} />
        <DetailRow label="Kupon" value={getCouponInfoText()} />
      </Box>

      <Typography
        onClick={() => setIsDialogOpen(true)}
        sx={{
          ...poppins,
          fontSize: "0.8rem",
          color: "#fff",
          textDecoration: "underline",
          cursor: "pointer",
          mb: 1.5,
          textAlign: "center",
        }}
      >
        Syarat dan ketentuan
      </Typography>

      <Box
        display="flex"
        alignItems="flex-start"
        gap={1}
        sx={{
          backgroundColor: "rgba(255,255,255,0.88)",
          border: "1px solid rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          borderRadius: "12px",
          p: 1.5,
          mb: 3,
        }}
      >
        <Checkbox
          checked={isConfirmed}
          onChange={(e) => setIsConfirmed(e.target.checked)}
          sx={{
            color: "rgba(30,58,110,0.4)",
            "&.Mui-checked": { color: "#1e3a6e" },
            p: 0,
            mt: 0.2,
          }}
        />

        <Typography
          sx={{
            ...poppins,
            fontSize: "0.82rem",
            color: "rgba(15,31,69,0.75)",
            lineHeight: 1.5,
          }}
        >
          Saya setuju dengan detail pesanan di atas dan ingin menyelesaikan
          pesanan.
        </Typography>
      </Box>

      {!isDisabled && (
        <Button
          variant="contained"
          fullWidth
          disabled={isButtonDisabled}
          onClick={handleDeal}
          startIcon={<HandshakeIcon />}
          sx={{
            ...poppins,
            background: "#1e3a6e",
            color: "#fff",
            borderRadius: "14px",
            py: 1.8,
            mb: 1.5,
            fontSize: "0.9rem",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "0 6px 24px rgba(15,31,69,0.4)",
            "&:hover": { background: "#162d57" },
            "&.Mui-disabled": {
              background: "#9aa5b8",
              color: "#fff",
            },
          }}
        >
          {getButtonText()}
        </Button>
      )}

      <TermDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />
    </Box>
  );
}
